// client/src/coms/serveradmin/OnboardingScreen.js
// What a new member sees before entering a server that has onboarding (or while waiting for approval):
// welcome, description, rules (+ acceptance), screening questions, interests, roles, recommended channels and
// an optional introduction. Used live (OnboardingGate) and as the preview in Server Settings → Onboarding.
import React, { useState } from 'react';
import { comsApiPost } from '../comsApi';
import { errorText } from './adminKit';

export default function OnboardingScreen({ serverKey, data, preview = false, onDone, onOpenChannel }) {
  const cfg = (data && data.config) || {};
  const state = (data && data.state) || {};
  const pending = state.status === 'pending';
  const [accept, setAccept] = useState(!!state.rules_accepted);
  const [answers, setAnswers] = useState(state.answers || {});
  const [interests, setInterests] = useState([]);
  const [roles, setRoles] = useState(() => (data && data.roles ? data.roles.filter((r) => r.mine).map((r) => r.id) : []));
  const [intro, setIntro] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const rolesById = new Map(((data && data.roles) || []).map((r) => [r.id, r]));
  const channels = (data && data.channels) || [];
  const channelName = (id) => (channels.find((c) => c.id === id) || {}).name;
  const needRules = (cfg.rules || []).length > 0 && (cfg.requireRules || (data && data.verification_level === 'rules'));
  const welcome = cfg.welcome || {};

  async function submit() {
    if (preview) return;
    setBusy(true);
    setErr('');
    try {
      const r = await comsApiPost(`/servers/${encodeURIComponent(serverKey)}/onboarding/complete`, { accept_rules: accept, answers, interests, roles, introduction: intro });
      setDone(true);
      if (typeof onDone === 'function') onDone(r);
    } catch (e) {
      setErr(errorText(e, 'Could not finish. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div className="vxa-onboard-card">
      <h1>{welcome.title || `Welcome to ${(data && data.server && data.server.name) || 'the server'}`}</h1>
      {welcome.body || (data && data.server && data.server.description) ? <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{welcome.body || data.server.description}</p> : null}
      {pending ? (
        <div className="vxa-flash is-warn" role="status">
          {state.completed || done ? 'Thanks! A moderator will review your request soon. You will be notified when you are approved.' : 'This server approves new members. Complete the steps below and a moderator will review your request.'}
        </div>
      ) : null}

      {(cfg.rules || []).length ? (
        <section className="vxa-card">
          <h3>Rules</h3>
          {cfg.rules.map((r, i) => (
            <div key={i} className="vxa-rule">
              <span className="vxa-rule-num">{i + 1}</span>
              <div>
                <b>{r.title}</b>
                {r.body ? <div className="vxa-small" style={{ whiteSpace: 'pre-wrap' }}>{r.body}</div> : null}
              </div>
            </div>
          ))}
          {needRules ? (
            <label className="vxa-row" style={{ marginTop: 10, fontWeight: 700 }}>
              <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} /> I have read and agree to the rules
            </label>
          ) : null}
        </section>
      ) : null}

      {(cfg.questions || []).length ? (
        <section className="vxa-card">
          <h3>A few questions</h3>
          {cfg.questions.map((q) => (
            <div key={q.id} className="vxo-field">
              <span>
                {q.prompt}
                {q.required ? ' *' : ''}
              </span>
              {q.type === 'text' ? (
                <input value={answers[q.id] || ''} maxLength={500} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
              ) : (
                q.options.map((o) => {
                  const cur = answers[q.id];
                  const on = q.type === 'multi' ? (cur || []).includes(o) : cur === o;
                  return (
                    <button
                      key={o}
                      type="button"
                      className="vxa-choice"
                      aria-pressed={on}
                      onClick={() => setAnswers({ ...answers, [q.id]: q.type === 'multi' ? (on ? (cur || []).filter((x) => x !== o) : [...(cur || []), o]) : o })}
                    >
                      {on ? '●' : '○'} {o}
                    </button>
                  );
                })
              )}
            </div>
          ))}
        </section>
      ) : null}

      {(cfg.interests || []).length ? (
        <section className="vxa-card">
          <h3>What are you interested in?</h3>
          {cfg.interests.map((it) => (
            <button key={it.id} type="button" className="vxa-choice" aria-pressed={interests.includes(it.id)} onClick={() => toggle(interests, setInterests, it.id)}>
              <span aria-hidden="true">{it.emoji || '•'}</span>
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.channel_ids && it.channel_ids.length ? <span className="vxa-muted vxa-small">{it.channel_ids.map((id) => channelName(id)).filter(Boolean).map((n) => `#${n}`).join(' ')}</span> : null}
            </button>
          ))}
        </section>
      ) : null}

      {(cfg.selfRoles || []).length ? (
        <section className="vxa-card">
          <h3>Pick your roles</h3>
          {cfg.selfRoles.map((r) => {
            const role = rolesById.get(r.role_id);
            if (!role && !preview) return null;
            return (
              <button key={r.role_id} type="button" className="vxa-choice" aria-pressed={roles.includes(r.role_id)} onClick={() => toggle(roles, setRoles, r.role_id)}>
                <span aria-hidden="true">{r.emoji || (role && role.icon) || '🏷️'}</span>
                <span style={{ flex: 1 }}>
                  <b>{r.label || (role && role.name) || `Role ${r.role_id}`}</b>
                  {r.description ? <div className="vxa-small vxa-muted">{r.description}</div> : null}
                </span>
              </button>
            );
          })}
        </section>
      ) : null}

      {cfg.introductions && cfg.introductions.enabled && !pending ? (
        <section className="vxa-card">
          <h3>Say hello</h3>
          <label className="vxo-field">
            {cfg.introductions.prompt}
            <textarea rows={3} maxLength={1000} value={intro} onChange={(e) => setIntro(e.target.value)} />
          </label>
          <div className="vxa-small vxa-muted">Optional — posted in #{channelName(cfg.introductions.channelId) || 'introductions'} as you.</div>
        </section>
      ) : null}

      {(cfg.recommendedChannels || []).length || (cfg.guidance || []).length ? (
        <section className="vxa-card">
          <h3>Getting started</h3>
          {(cfg.guidance || []).map((g, i) => (
            <div key={i} className="vxa-small" style={{ padding: '2px 0' }}>
              ✓ {g}
            </div>
          ))}
          <div className="vxa-row" style={{ marginTop: 6 }}>
            {(cfg.recommendedChannels || []).map((id) =>
              channelName(id) ? (
                <button key={id} type="button" className="vx-btn small" disabled={preview || pending} onClick={() => typeof onOpenChannel === 'function' && onOpenChannel(id)}>
                  #{channelName(id)}
                </button>
              ) : null
            )}
          </div>
        </section>
      ) : null}

      {err ? <div className="vxa-flash is-error">{err}</div> : null}
      {!(pending && (state.completed || done)) ? (
        <button type="button" className="vx-btn primary" style={{ height: 40, padding: '0 20px' }} disabled={busy || preview || (needRules && !accept)} onClick={submit}>
          {pending ? 'Send my answers' : 'Enter the server'}
        </button>
      ) : null}
      {preview ? <div className="vxa-small vxa-muted" style={{ marginTop: 6 }}>Preview — buttons are inactive.</div> : null}
    </div>
  );
}
